import { PlusOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Divider, Spin, Table, Popconfirm } from 'antd';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdminRole, isContentSupervisorRole, isContentSpecialistRole } from '~helpers/auth';
import { NotificationBlanktip as NotificationEmptyIcon } from 'common/components/svgs';
import { dayFormatType, formatDay } from '~helpers/day';
import { TableSearchInput } from 'common/components/table-filter-component';
import NotificationViewer from './notification-viewer';
import NotificationEditor from './notification-editor';
import { useData, NotificationStatusLabel } from './useData';

/**
 *
 * @param {string|null|undefined} text need trim text.
 * @returns {string|null}
 */
const removeSpace = text => {
  if (text) {
    return text.trim() || null;
  }
  return text;
};

const getDuration = (publishType, start, end) => {
  switch (publishType) {
    case 'PERMANENT':
      return 'Permanent';
    case 'ONE_TIME':
      return formatDay(dayFormatType.UKWITHHMS, start);
    case 'RANGE':
      return `${formatDay(dayFormatType.UKWITHHMS, start)} - ${formatDay(
        dayFormatType.UKWITHHMS,
        end,
      )}`;

    default:
      return '-';
  }
};

function Notification() {
  const { t } = useTranslation();

  const [editingNotification, setEditingNotification] = useState(null);
  const [viewingNotification, setViewingNotification] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showNameSearch, setShowNameSearch] = useState(false);

  const havePermissionToMutation =
    isAdminRole() || isContentSpecialistRole() || isContentSupervisorRole();

  const {
    data: list,
    totalCount,
    filter,
    loading,
    pagination,
    setPagination,
    setQueryFilter,
    refetch,
    deleteItem,
  } = useData();

  const isValidFilter = filter.name || filter.publishType || filter.status;

  const handleCloseEditorModal = (needRefetch = false) => {
    setShowEditor(false);
    typeof needRefetch === 'boolean' && needRefetch && refetch({ resetAll: true });
  };

  const handleCloseViewerModal = () => {
    setViewingNotification(null);
  };

  const handleCreateNotification = () => {
    setEditingNotification(null);
    setShowEditor(true);
  };

  const handleEditNotification = item => {
    handleCloseViewerModal();
    setEditingNotification(item);
    setShowEditor(true);
  };

  const handleViewNotification = data => {
    setViewingNotification(data);
  };

  const tableColumns = [
    {
      title: t('cms.marketing.notification.list.column.name'),
      dataIndex: 'node.name',
      key: 'name',
      className: 'notification-list__body__list__column',
      width: '14%',
      render: text => (
        <span title={text} className="list-template__body__list__column-body">
          {text || '-'}
        </span>
      ),
      filterDropdown: (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            field="name"
            value={filter.name || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, name: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowNameSearch(false);
            }}
          />
        </div>
      ),
      filterIcon: <SearchOutlined style={{ color: filter.name ? '#38b2a6' : '#c8c9cb' }} />,
      filterDropdownVisible: showNameSearch,
      onFilterDropdownVisibleChange: view => {
        setShowNameSearch(view);
      },
    },
    {
      title: (
        <div className="notification-list__body__list__column-header">
          {t('cms.marketing.notification.list.column.message_title')}
        </div>
      ),
      dataIndex: 'node.messageTitle',
      key: 'messageTitle',
      className: '',
      width: '20%',
      render: text => (
        <span title={text} className="list-template__body__list__column-body">
          {text || '-'}
        </span>
      ),
    },
    {
      title: (
        <div className="notification-list__body__list__column-header">
          {t('cms.marketing.notification.list.column.status')}
        </div>
      ),
      dataIndex: 'node.status',
      key: 'status',
      width: '12%',
      className: 'notification-list__body__list__column',
      filterMultiple: false,
      filters: [
        { text: 'Active', value: 'ACTIVE' },
        { text: 'Expired', value: 'EXPIRED' },
        { text: 'Scheduled', value: 'SCHEDULED' },
      ],
      filteredValue: [filter.status] || [],
      filterIcon: (
        <FilterOutlined style={{ color: filter.status.length ? '#38b2a6' : '#c8c9cb' }} />
      ),
      render: text => {
        return (
          <span
            className={classNames(
              'notification-list__status-dot list-template__body__list__column-body',
              `notification-list__status-dot--${text}`,
            )}
          >
            {NotificationStatusLabel[text]}
          </span>
        );
      },
    },
    {
      title: (
        <div className="notification-list__body__list__column-header">
          {t('cms.marketing.notification.list.column.time_duration')}
        </div>
      ),
      key: 'timeDuration',
      className: 'notification-list__body__list__column',
      width: '16%',
      render: (_text, record) => (
        <span className="list-template__body__list__column-body">
          {getDuration(record.node.publishType, record.node.publishStart, record.node.publishEnd)}
        </span>
      ),
    },
    {
      title: t('cms.marketing.notification.list.column.update_time'),
      key: 'updateTime',
      className: 'notification-list__body__list__column',
      width: '12%',
      render: (text, record) => (
        <span className="list-template__body__list__column-body">
          {`${formatDay(dayFormatType.UKWITHHMS, record.node.updatedAt)}`}
        </span>
      ),
    },
    {
      title: t('cms.marketing.notification.list.column.edited_by'),
      dataIndex: 'node.updatedBy',
      key: 'updatedBy',
      className: '',
      width: '12%',
      render: (_text, record) => {
        return (
          <span className="list-template__body__list__column-body">
            {record.node.updatedByCmsUser.firstName} {record.node.updatedByCmsUser.lastName}
          </span>
        );
      },
    },
    {
      title: t('cms.marketing.notification.list.column.action'),
      dataIndex: 'actions',
      key: 'actions',
      width: '14%',
      render: (text, record) => (
        <div className="list-template__body__list__column-body">
          <a role="button" tabIndex="0" onClick={() => handleViewNotification(record.node)}>
            View
          </a>
          <If condition={havePermissionToMutation}>
            <Divider type="vertical" />
            <a role="button" tabIndex="1" onClick={() => handleEditNotification(record.node)}>
              Edit
            </a>
            <Divider type="vertical" />
            <Popconfirm
              title="Are you sure delete this notification?"
              onConfirm={() => {
                deleteItem({ variables: { input: { id: record.node.id } } });
              }}
              okText="Yes"
              cancelText="No"
            >
              <a role="button" tabIndex="2" className="notification-list__color-red" href="#">
                Delete
              </a>
            </Popconfirm>
          </If>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="notification-list list-template">
        <div className="notification-list__header list-template__header">
          <h1>{t('cms.marketing.notification.title')}</h1>
          <If condition={havePermissionToMutation}>
            {list.length ? (
              <button
                className="list-template__header__button"
                type="button"
                onClick={handleCreateNotification}
              >
                <PlusOutlined
                  className="list-template__header__button__icon"
                  style={{ fontSize: '12px', marginLeft: '4px' }}
                />
                {t('cms.marketing.notification.button.add')}
              </button>
            ) : null}
          </If>
        </div>

        <section
          className={classNames('notification-list__body list-template__body', {
            'list-template__body--bg-white': list.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={loading && !isValidFilter && list.length === 0}>
              <div className="list-template__spin-container">
                <Spin />
              </div>
            </When>
            <When condition={list.length || isValidFilter}>
              <Table
                columns={tableColumns}
                className="table-list notification-list__body__list"
                dataSource={list}
                rowKey={record => record.node.id}
                // scroll={{ x: '130%' }}
                pagination={{
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  total: totalCount,
                }}
                loading={loading}
                onChange={(newestPagination, filter) => {
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  });
                  setQueryFilter(current => ({ ...current, status: filter.status[0] || '' }));
                }}
              />
            </When>
            <When condition={list.length === 0 && !isValidFilter && !loading}>
              <div className="list-template__blank-tips">
                <div className="list-template__blank-tips__content">
                  <div>
                    <NotificationEmptyIcon />
                  </div>
                  <span className="list-template__blank-tips__tip">
                    {t('cms.marketing.notification.blanktip')}
                  </span>
                  <If condition={havePermissionToMutation}>
                    <button
                      className="list-template__header__button"
                      type="button"
                      onClick={handleCreateNotification}
                    >
                      <PlusOutlined
                        className="list-template__header__button__icon"
                        style={{ fontSize: '12px', marginLeft: '4px' }}
                      />
                      {t('cms.marketing.notification.button.add')}
                    </button>
                  </If>
                </div>
              </div>
            </When>
          </Choose>
        </section>

        <If condition={showEditor}>
          <NotificationEditor
            activeModal
            editingNotification={editingNotification}
            handleCloseModal={handleCloseEditorModal}
          />
        </If>

        <If condition={viewingNotification}>
          <NotificationViewer
            activeModal
            viewingNotification={viewingNotification}
            handleCloseModal={handleCloseViewerModal}
            openEditor={() => handleEditNotification(viewingNotification)}
          />
        </If>
      </div>
    </>
  );
}

export default Notification;
